import { Fragment } from "react";
import { useAuthContext } from "../../../components/Auth";
import type { DataMutate } from "../../../types/types";
import type {
  PIMProduct,
  SupportedAssetAccessibility,
  TemplateTabData,
} from "../../../types/types.PIM";
import { CollectionComponent } from "./Collections/CollectionComponent";
import { ProductGroupCard } from "./ProductOverview/ProductGroupCard";
import { ProductVariants } from "./ProductOverview/ProductVariants";
import styled from "styled-components/macro";
import { useStoreState } from "../../../util/util";

const ProductVariantsSeparator = styled.div<{
  pageContainerSettings: "single_container" | "no_containers";
}>`
  height: 2px;
  border-top: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
  padding-top: ${({ theme, pageContainerSettings }) =>
    pageContainerSettings === "no_containers"
      ? `${theme.resolve("spacing_2xl")}`
      : 0};
  margin: ${({ theme, pageContainerSettings }) =>
    pageContainerSettings === "single_container"
      ? `0 ${theme.resolve("spacing_2xl")}`
      : 0};
`;

interface ITabPanelProps {
  product: PIMProduct;
  tab: TemplateTabData;
  mutateProduct: DataMutate<PIMProduct>;
  replaceProductId: (id: string) => void;
  isPortfolio: boolean;
}

export const ProductTabPanel = ({
  product,
  tab,
  isPortfolio = false,
  mutateProduct,
  replaceProductId,
}: ITabPanelProps) => {
  const {
    roleIsSomeKindOfSeller,
    roleIsGuest,
    hasPermission,
    roleIsSomeKindOfBuyer,
  } = useAuthContext();
  const { storefront_metadata } = useStoreState();
  const pageContainerSettings =
    storefront_metadata.theme_object.designTokens?.productDetail
      ?.container_settings?.value || "no_containers";

  // Determine if we should hide empty content (for portfolio or sellers without edit permissions)
  const shouldHideEmptyContent =
    isPortfolio ||
    (roleIsSomeKindOfSeller &&
      !(hasPermission("modify_products") && product.is_editable));

  return (
    <>
      {tab.items.map((item) => {
        if (
          item.type === "collection" &&
          !!product.product_schema.collections.find((collection) =>
            collection.name.toLowerCase().includes(item.name.toLowerCase())
          )
        ) {
          const collection = product.product_schema.collections.find(
            (collection) =>
              collection.name.toLowerCase() === item.name.toLowerCase()
          );
          const showCollection =
            collection?.name.toLowerCase() !== "skus" &&
            collection?.is_visible &&
            ((shouldHideEmptyContent &&
              collection.value_rows &&
              collection.value_rows.length > 0 &&
              collection.value_rows.flatMap(({ values }) => values).length >
                0 &&
              (!collection.is_restricted ||
                (collection.is_restricted && roleIsSomeKindOfSeller))) ||
              !shouldHideEmptyContent);

          return (
            <Fragment key={item.id}>
              {!!collection && showCollection && (
                <CollectionComponent
                  collection={collection}
                  product={product}
                  fetchProductData={mutateProduct}
                  replaceProductId={replaceProductId}
                  isPortfolio={isPortfolio}
                />
              )}
            </Fragment>
          );
        } else {
          const group = product.product_schema.groups.find(
            (group) => group.name.toLowerCase() === item.name.toLowerCase()
          );

          const groupHasValues = () => {
            let hasValues = false;
            const is_accessible = (
              accessibility?: SupportedAssetAccessibility
            ) => {
              if (roleIsGuest) {
                return accessibility === "public";
              }
              if (roleIsSomeKindOfBuyer) {
                return accessibility !== "internal";
              }
              return true;
            };
            group?.attributes?.forEach((attribute) => {
              if (
                (attribute.values?.length > 0 &&
                  shouldHideEmptyContent &&
                  !attribute.is_restricted &&
                  !attribute.is_not_applicable &&
                  attribute.is_visible &&
                  is_accessible(attribute.values[0]?.visibility ?? "public")) ||
                (attribute.is_visible &&
                  roleIsSomeKindOfSeller &&
                  !shouldHideEmptyContent)
              ) {
                hasValues = true;
              }
            });
            return hasValues;
          };
          const showGroup =
            (!!group?.attributes?.length &&
              group.name.toLowerCase() !== "seo" &&
              groupHasValues()) ||
            (!isPortfolio &&
              groupHasValues() &&
              group?.name.toLowerCase() !== "seo");
          return (
            <Fragment key={item.id}>
              {!!group && showGroup && (
                <ProductGroupCard
                  product={product}
                  group={group}
                  key={group.id}
                  mutateProduct={mutateProduct}
                  isPortfolio={isPortfolio}
                  replaceProductId={replaceProductId}
                />
              )}
            </Fragment>
          );
        }
      })}
      {roleIsSomeKindOfSeller &&
        tab.tab_type === "overview" &&
        tab.is_enabled &&
        product.pim_product_type === "master" && (
          <>
            {pageContainerSettings !== "individual_containers" && (
              <ProductVariantsSeparator
                pageContainerSettings={pageContainerSettings}
              />
            )}
            <ProductVariants product={product} />
          </>
        )}
    </>
  );
};
